import type { HelpCategory } from "../../types";

export const settings: HelpCategory = {
  id: "settings",
  title: "Настройки и аккаунт",
  description: "Компания и филиалы, подписка и дополнения, уведомления и Telegram, безопасность, журнал аудита и поддержка.",
  icon: "settings",
  articles: [
    {
      id: "company-branches",
      title: "Компания и филиалы",
      summary: "Реквизиты компании, филиалы с адресом, часовым поясом, валютой, налогом, форматами работы и временем начала бизнес-дня.",
      path: "/branches",
      permission: "branches.view",
      keywords: ["компания", "филиал", "часовой пояс", "валюта", "налог", "бизнес-день", "форматы", "карта", "геолокация", "рядом", "маршрут", "контакты"],
      related: ["onboarding", "subscription"],
      body: [
        { t: "p", text: "[«Компания»](/settings/company) — название, реквизиты, страна и настройки по умолчанию для новых филиалов. Там же — свой бренд (если подключён)." },
        { t: "tip", text: "**Контакты и места на карте** (в «Компании» и во вкладке «На карте» у филиала) — где филиал на карте: нажмите на карту, найдите по адресу или возьмите своё местоположение. Точка нужна для страницы «Рядом» на сайте Coca (заведения и их акции на карте) и для кнопки «Маршрут» на вашем сайте. Телефон и соцсети для гостей — в [«Сайт и QR-меню»](/settings/site)." },
        {
          t: "terms", title: "Настройки филиала",
          items: [
            { term: "Часовой пояс", text: "По нему считаются «сегодня», отчёты по часам и смены." },
            { term: "Начало бизнес-дня", text: "Например 06:00: заказы ночью до 06:00 относятся к вчерашнему дню. Незакрытые рабочие смены закрываются в это время." },
            { term: "Валюта и налог", text: "Валюта цен, ставка налога и включён ли он в цену. Меняется, когда нет открытых заказов." },
            { term: "Форматы", text: "В зале, с собой, доставка — какие типы заказа доступны на кассе." },
            { term: "Активен", text: "Выключенный филиал только для чтения: новые заказы и документы в нём невозможны." },
          ],
        },
        { t: "warn", text: "Число филиалов ограничено тарифом." },
      ],
    },
    {
      id: "subscription",
      title: "Подписка, тарифы и дополнения",
      summary: "Ваш тариф и лимиты, как подключить дополнение или сменить тариф, счета и что означают статусы подписки.",
      path: "/settings/subscription",
      permission: "subscription.view",
      keywords: ["подписка", "тариф", "дополнение", "оплата", "счёт", "лимит", "замок", "подключить", "пробный период"],
      related: ["overview", "support"],
      body: [
        {
          t: "steps", title: "Подключить дополнение",
          items: [
            "Нажмите на раздел с замком в меню — откроется описание и кнопка **«Подключить»**. Или [«Подписка»](/settings/subscription) → «Дополнения».",
            "Система покажет сумму с учётом оставшихся дней периода — подтвердите.",
            "Если доплата нужна, выставится **счёт** с реквизитами; дополнение включится после оплаты. Без доплаты (пробный период) — сразу.",
          ],
        },
        {
          t: "terms", title: "Статусы подписки",
          items: [
            { term: "Пробный период", text: "Всё работает, внизу видно, сколько дней осталось." },
            { term: "Активна", text: "Оплачена." },
            { term: "Просрочена", text: "Счёт не оплачен, но несколько дней всё работает." },
            { term: "Только чтение", text: "Касса продаёт, но настройки и отчёты закрыты." },
            { term: "Приостановлена", text: "Работа остановлена до оплаты. Писать в поддержку можно всегда." },
          ],
        },
        { t: "tip", text: "Переход на меньший тариф происходит с начала следующего периода — система заранее проверит, не превышены ли лимиты." },
      ],
    },
    {
      id: "notifications",
      title: "Уведомления и Telegram-бот",
      summary: "Колокольчик в шапке, уведомления на почту и в Telegram, дневной отчёт; какие типы приходят и как их отключить.",
      path: "/settings/notifications",
      keywords: ["уведомления", "telegram", "телеграм", "бот", "почта", "колокольчик", "дневной отчёт"],
      related: ["dashboard-reports", "expiry", "suppliers"],
      body: [
        {
          t: "steps", title: "Подключить Telegram",
          items: [
            "[«Уведомления»](/settings/notifications) → **«Подключить Telegram»** — откроется бот.",
            "Нажмите «Start» в Telegram — привязка готова (ссылка действует 15 минут).",
            "Команды бота: **/today** — итоги дня, **/stop** — отключить.",
          ],
        },
        {
          t: "list", title: "Что можно настроить",
          items: ["Каналы: почта, Telegram.", "Дневной отчёт — каждый вечер по филиалам.", "Какие типы получать: заканчивается на складе, сроки годности, закрытие смены, возвраты, QR-заказы, вызов официанта, оплаты поставщикам, заявки на склад, подозрительные действия и др.", "Тип можно отключить совсем или только для Telegram/почты."],
        },
        { t: "tip", text: "Каждый видит только уведомления по своим правам: официанту — вызовы и QR-заказы, бухгалтеру — оплаты поставщикам." },
      ],
    },
    {
      id: "security",
      title: "Безопасность: двухфакторный вход, сеансы, пароль",
      summary: "Защита аккаунта кодом из приложения-аутентификатора, резервные коды, активные сеансы и восстановление пароля.",
      path: "/settings/security",
      keywords: ["безопасность", "2fa", "двухфакторная", "аутентификатор", "пароль", "сброс пароля", "сеансы", "выйти везде"],
      related: ["users-roles", "audit"],
      body: [
        {
          t: "steps", title: "Включить двухфакторную защиту",
          items: ["[«Безопасность»](/settings/security) → «Включить двухфакторную защиту».", "Отсканируйте QR в Google Authenticator / Яндекс Ключ.", "Введите 6 цифр из приложения.", "Сохраните **10 резервных кодов** — каждый работает один раз, если телефона нет под рукой."],
        },
        { t: "p", text: "**Активные сеансы** — где выполнен вход; можно завершить любой или **«Выйти на всех устройствах»**." },
        { t: "p", text: "**Забыли пароль** — на странице входа «Забыли пароль?»: ссылка придёт на почту и действует 30 минут. После смены пароля выход происходит на всех устройствах. Ссылку можно отправить и не выходя: в окне, где спрашивают пароль (например, отключение двухфакторной защиты), — «Забыли пароль?»; сотруднику — «Сотрудники» → «Отправить ссылку для сброса пароля»." },
      ],
    },
    {
      id: "audit",
      title: "Журнал аудита",
      summary: "Кто, что и когда изменил: цены, отмены, возвраты, кассовые операции, долги, смены, зарплата, склад.",
      path: "/settings/audit",
      permission: "audit.view",
      keywords: ["аудит", "журнал", "история изменений", "кто изменил"],
      related: ["fraud", "security"],
      body: [
        { t: "p", text: "[«Журнал аудита»](/settings/audit) записывает каждое важное действие: кто, когда, в каком филиале, что было и что стало. Фильтры по сотруднику, разделу и периоду. Записи нельзя изменить или удалить." },
        { t: "tip", text: "Ищете, кто поменял цену? Фильтр «Меню» + название позиции." },
      ],
    },
    {
      id: "report-reset",
      title: "Сброс отчётов",
      summary: "Начать учёт с чистого листа с выбранной даты: данные не удаляются, отчёты за прошлое — в архиве Excel/PDF.",
      path: "/settings/report-reset",
      permission: "report_resets.view",
      feature: "core.analytics_basic",
      keywords: ["сброс", "обнулить", "очистить отчёты", "выручка с нуля", "архив", "новый месяц"],
      related: ["audit", "dashboard-reports"],
      body: [
        { t: "p", text: "[«Сброс отчётов»](/settings/report-reset) нужен, когда учёт надо начать заново — после обучения и тестовых продаж или с нового месяца. С выбранной даты все отчёты (выручка, финансы, блюда, официанты, ИИ-подсказки) считаются с нуля. **Ничего не удаляется**: заказы, оплаты, смены, документы, склад, долги клиентов и поставщикам остаются как были." },
        { t: "steps", items: ["**«Сбросить отчёты»** → выберите дату (не позже сегодня) и проверьте, что изменится.", "**«Архив»** — скачайте отчёты за прошлый период: «Печать», «Сохранить PDF» или «Скачать Excel». Отметьте, что сохранили архив.", "**«Подтверждение»** — введите свой пароль, затем в окне — слово-подтверждение."] },
        { t: "warn", text: "Сбрасывать может только владелец или администратор компании (право «Сброс отчётов»). В сеансе поддержки сброс недоступен." },
        { t: "tip", text: "Ошиблись? В «Истории сбросов» последний сброс можно отменить (с паролем) — отчёты снова увидят прошлое. Архив любого сброса можно скачать снова или удалить (**«Удалить архив»**, с паролем) — сам сброс и запись в истории останутся." },
      ],
    },
    {
      id: "support",
      title: "Поддержка Coca",
      summary: "Как написать в поддержку и получить ответ прямо в программе.",
      path: "/support",
      permission: "support.view",
      keywords: ["поддержка", "помощь", "вопрос", "обращение", "тикет"],
      related: ["subscription"],
      body: [
        { t: "steps", items: ["[«Поддержка»](/support) → **«Написать в поддержку»**: тема, описание, при необходимости скриншот.", "Ответ придёт в этот раздел и уведомлением (в колокольчик, на почту, в Telegram).", "С дополнением «Быстрая поддержка» срок ответа — 2 часа, без него — до 24 часов."] },
        { t: "tip", text: "Писать в поддержку можно даже при приостановленной подписке." },
      ],
    },
  ],
};
